import { useState } from 'react';
import { z } from 'zod';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Button, Fade, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import { InnerMaxWidthContainer } from '../layouts/InnerMaxWidthContainer';
import { OnboardingLoginWrapper } from '../layouts/OnboardingLoginWrapper';
import joinStep1Lottie from 'src/assets/animations/join-step1-mobile.lottie?url';
import joinStep2Lottie from 'src/assets/animations/join-step2-mobile.lottie?url';
import joinStep3Lottie from 'src/assets/animations/join-step3-mobile.lottie?url';
import FullBleedHero from 'src/components/FullBleedHero';
import StepIndicator from 'src/components/StepIndicator';
import useResponsive from 'src/hooks/useResponsive';

interface StepContent {
    lottieSrc: string;
    heading: string;
    body: string;
}

const steps: StepContent[] = [
    {
        lottieSrc: joinStep1Lottie,
        heading: 'Welcome to\nSage Home',
        body: 'We give peace of mind and visibility to caregivers managing care at home.',
    },
    {
        lottieSrc: joinStep2Lottie,
        heading: 'Stay Connected',
        body: 'Keep everyone in the loop with real-time updates.',
    },
    {
        lottieSrc: joinStep3Lottie,
        heading: 'Share Responsibility',
        body: 'Get everyone involved by organizing care needs in one place.',
    },
];

const joinSearchSchema = z.object({
    step: z.coerce.number().min(0).max(2).default(0),
});

export const Route = createFileRoute('/join')({
    component: RouteComponent,
    validateSearch: zodValidator(joinSearchSchema),
});

function RouteComponent() {
    const { spacing, shadows } = useTheme();
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const { step } = Route.useSearch();
    const [isTransitioning, setIsTransitioning] = useState(false);

    const currentStep = step;
    const currentContent = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;

    const handleNext = () => {
        if (isLastStep) {
            navigate({ to: '/home' });
            return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            navigate({
                to: '/join',
                search: { step: currentStep + 1 },
            });
            setIsTransitioning(false);
        }, 300);
    };

    return (
        <OnboardingLoginWrapper>
            <InnerMaxWidthContainer
                wrapperPropsSx={{
                    width: isMobile ? 'auto' : spacing(60),
                    boxShadow: isMobile ? 'none' : shadows[2],
                    backgroundColor: 'purple.50',
                    pb: isMobile ? 0 : 4,
                }}
            >
                <Stack
                    sx={{
                        height: isMobile ? '100svh' : spacing(90),
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <FullBleedHero>
                        <DotLottieReact
                            src={currentContent.lottieSrc}
                            autoplay
                            layout={{ fit: 'fit-width' }}
                            key={currentStep}
                        />
                    </FullBleedHero>

                    <Stack sx={{ gap: 2.5, pb: isMobile ? 2.5 : 0 }}>
                        <Fade in={!isTransitioning} timeout={300}>
                            <Stack sx={{ gap: 1, alignItems: 'center', textAlign: 'center' }}>
                                <Typography
                                    variant="h1"
                                    sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}
                                >
                                    {currentContent.heading}
                                </Typography>
                                <Typography variant="body1">{currentContent.body}</Typography>
                            </Stack>
                        </Fade>

                        <Stack
                            direction="row"
                            sx={{ justifyContent: 'center', alignItems: 'center', gap: 1 }}
                        >
                            {steps.map((_, index) => (
                                <StepIndicator key={index} isActive={index === currentStep} />
                            ))}
                        </Stack>

                        <Button variant="contained" color="primary" fullWidth onClick={handleNext}>
                            {isLastStep ? 'Get Started' : 'Next'}
                        </Button>
                    </Stack>
                </Stack>
            </InnerMaxWidthContainer>
        </OnboardingLoginWrapper>
    );
}
